import { DOCUMENT, isPlatformServer, NgOptimizedImage, NgStyle } from '@angular/common';
import {
  Component,
  Inject,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectionStrategy,
  afterNextRender,
  PLATFORM_ID,
  inject,
  input,
  NgZone,
} from '@angular/core';
import Splide from '@splidejs/splide';
import { MISSING_PRODUCT_IMAGE_URL } from '../../../app.tokens';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { ProductGallerySubset } from '../product-gallery/product-gallery.component';

/**
 * Wrapped Product Gallery Component
 * This component uses the splide library to create a carousel for the product images.
 * Splide modifies heavily the underlying DOM significantly so this component can't be updated in place
 */
@Component({
  standalone: true,
  selector: 'app-splide-product-gallery',
  styleUrls: ['./splide-product-gallery.component.scss'],
  templateUrl: './splide-product-gallery.component.html',
  imports: [NgStyle, NgOptimizedImage],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppSplideProductGalleryComponent implements OnChanges, OnDestroy {
  product = input.required<ProductGallerySubset>();
  isProductPage = input(false);
  showLowestPriceTag = input(false);
  defaultAltText = input('');
  @ViewChild('thumbnailsPresentation') thumbnailsPresentation!: ElementRef;

  gallerySettingsByProduct = new Map<string, any>();

  mainImageHeight: number | undefined;
  thumbnailDesktop: number = 82;
  thumbnailGap: number = 28;

  #zone = inject(NgZone);
  #platformId = inject(PLATFORM_ID);

  main: Splide | null = null;
  thumbnails: Splide | null = null;

  private resizeObserver?: ResizeObserver;

  #logger = inject(GenericLogger);
  #initialChanges = false;

  constructor(
    @Inject(DOCUMENT) private _doc: Document,
    @Inject(MISSING_PRODUCT_IMAGE_URL) public missingProductImageUrl: string,
  ) {
    afterNextRender(() => {
      this.#initializeSlide();
    });
  }

  #initializeSlide(): void {
    if (isPlatformServer(this.#platformId)) {
      return;
    }
    this.#logger.debug(`Initializing Splide Slide for product ${this.product().id}`);
    let slideCount = this.product().gallery.length;
    let mainId = '#main-carousel_' + this.product().id;
    let thumbnailsId = '#main-carousel__thumbnails-' + this.product().id;
    // Initialize the Splide carousel trying to not impact the main thread
    this.#zone.runOutsideAngular(() => {
      this.main = new Splide(mainId, {
        type: 'fade',
        arrows: false,
        keyboard: false,
        perPage: 1,
        perMove: 1,
        isNavigation: false,
        pagination: false,
        role: 'group',
      });
      this.thumbnails = new Splide(thumbnailsId, {
        perPage: 5,
        fixedWidth: '20%',
        arrows: false,
        role: 'group',
        type: slideCount < 5 ? 'slide' : 'loop',
        keyboard: false,
        isNavigation: true,
        pagination: false,
        cover: true,
        direction: 'ltr',
        mediaQuery: 'min',
        breakpoints: {
          1200: {
            direction: 'ttb',
            fixedWidth: this.thumbnailDesktop,
            fixedHeight: this.thumbnailDesktop,
            height: 522,
            gap: this.thumbnailGap,
          },
          1400: {
            fixedWidth: 102,
            fixedHeight: 102,
            height: 612,
          },
        },
      });

      this.main.sync(this.thumbnails);
      this.main.mount();
      this.thumbnails.mount();
    });

    this.thumbnailsPresentation.nativeElement.ariaOrientation = null;

    this.resizeObserver = new ResizeObserver((entries) => {
      if (entries[0].contentRect.height !== this.mainImageHeight) {
        this.#setSlideHeight(entries[0].contentRect.height);
      }
      this.thumbnailsPresentation.nativeElement.ariaOrientation = null;
    });
    if (this._doc.querySelector('.main-carousel__item')) {
      this.resizeObserver.observe(this._doc.querySelector('.main-carousel__item')!);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.#initialChanges) {
      this.#logger.error({ msg: 'Updates to gallery are not supported (splide needs a full re-render)', changes });
    }
    this.#initialChanges = true;
  }

  #setSlideHeight(height: number) {
    if (height > 0) this.mainImageHeight = height;
  }

  ngOnDestroy(): void {
    this.#logger.debug('Destroying Splide Slide (noop on server)');
    this.main?.destroy();
    this.thumbnails?.destroy();
    this.resizeObserver?.disconnect();
  }
}
